import * as React from "react";
import * as analytics from '../../../analytics';
import { connect } from "react-redux";
import { IMondayComBoard, actionCreators, IMondayComConnectionState } from "../../../store/integration/MondayComStore";
import { IImportProjectMap } from "../../../store/integration/common";
import { ApplicationState } from "../../../store/index";
import { useDataLoadingAfterConnectionValidationEffect, IImportEntity, IImportEntityMap, getValidationError } from "./common";
import { ProjectsImportProps } from "./ProjectsImportPanel";
import { UserState } from "../../../store/User";

type OwnProps = {
    connectionId: string;
    onRender: (props: ProjectsImportProps) => JSX.Element;
}

type StateProps = {
    user: UserState;
    boards: IMondayComBoard[];
    isLoading: boolean;
    error: string | null;
    isImporting: boolean;
    connections: IMondayComConnectionState;
}

type Props = OwnProps & StateProps & typeof actionCreators;
const MondayComProjectImport = (props: Props) => {
    const { connectionId } = props;
    const entities = React.useMemo(() => props.boards.map<IImportEntity>(_ => ({
        id: `${_.boardId}`,
        name: _.boardName,
        fullName: `${_.boardName} (${_.boardId})`,
        isAlreadyLinked: _.isAlreadyLinked
    })), [props.boards]);
    const map = React.useMemo(() => props.boards.reduce((m, _) => ({ ...m, [`${_.boardId}`]: _ }), {}), [props.boards]);
    const _onImport = React.useCallback((connectionId: string, maps: IImportEntityMap[]) => {
        props.importProjects(connectionId, maps.map<IImportProjectMap<IMondayComBoard>>(_ => ({
            linkingData: map[_.entityId],
            projectId: _.projectId,
            projectName: _.projectName
        })));
        analytics.trackImport('Import Boards from Monday.com', props.user, { count: maps.length });
    }, [map]);

    const connectionValidation = useDataLoadingAfterConnectionValidationEffect(
        connectionId, 
        props.connections, 
        props.isImporting, 
        props.verifyConnection,
        props.loadBoards
    );

    return props.onRender({
        label: "Boards",
        connectionId: props.connectionId,
        isLoading: props.isLoading,
        error: getValidationError(connectionValidation) ?? props.error,
        onImport: _onImport,
        oldImportGridProps: {
            entities: entities,
            getSourcesInfo: __ => __.sourceInfos.map(_ => ({
                sourceId: _.sourceData.boardId,
                connectionId: _.connectionId,
                data: _
            }))
        }
    });
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        user: state.user,
        boards: state.mondayCom.boards.entities,
        isLoading: state.mondayCom.boards.isLoading,
        error: state.mondayCom.boards.error,
        isImporting: state.import.projects.isImporting,
        connections: state.mondayCom.connections
    }
}

export default connect(mapStateToProps, actionCreators)(MondayComProjectImport);